import { Box, Stack } from '@mui/material'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { EmptyList } from 'components/core/empty-list'
import List from 'components/core/list'
import { C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { C1TableReduxHook } from 'components/core/list/C1Table/C1TableReduxHook'
import { SelectOneAppHook } from 'components/core/search/apps'
import { SelectOneAppEntitlementHook } from 'components/core/search/SearchAppEntitlements'
import { RelativeTimestamp } from 'components/core/time/display'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { ExportData } from 'components/pages/explore/query/ExportData'
import { AppValue } from 'lib/actions/apps'
import { AppEntitlementUserBindingHistoryValue, AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { AppEntitlementWithExpired } from 'pbts/c1/api/app/v1/app_entitlement'
import { SearchPastGrantsRequest } from 'pbts/c1/api/app/v1/app_entitlement_user_binding'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { searchPastGrantsApi } from 'redux_toolkit/entitlements'

const cols: C1TableReduxColumn<AppEntitlementUserBindingHistoryValue>[] = [
    {
        field: 'appUserId',
        flex: 2,
        label: <FormattedMessage defaultMessage="Account" id="components_past_grants_account" />,
        Component: ({ row: grant }) => <AppUserDisplayName appUser={grant.appUser} />,
    },
    {
        field: 'app',
        flex: 2,
        label: <FormattedMessage defaultMessage="App" id="components_past_grants_app" />,
        Component: ({ row: grant }) => grant.app && <AppDisplayName app={grant.app} />,
    },
    {
        field: 'entitlement',
        flex: 2,
        label: <FormattedMessage defaultMessage="Entitlement" id="components_past_grants_entitlement" />,
        Component: ({ row: grant }) => (
            grant.appEntitlementId && grant.appId ?
                <C1EntitlementDisplayNameWithFetch
                    entitlementId={grant.appEntitlementId}
                    appId={grant.appId}
                    textTruncationWidth="400px"
                /> :
                <C1EmptyIndicator />
        ),
    },
    {
        field: 'discovered',
        label: <FormattedMessage defaultMessage="Granted" id="components_past_grants_granted" />,
        Component: ({ row: grant }) => (
            grant.grantedAt
                ? <RelativeTimestamp date={grant.grantedAt} variant="date" />
                : <C1EmptyIndicator />
        ),
    },
    {
        field: 'removed',
        label: <FormattedMessage defaultMessage="Removed" id="components_past_grants_removed" />,
        Component: ({ row: grant }) => (
            grant.revokedAt
                ? <RelativeTimestamp date={grant.revokedAt} variant="date" />
                : <C1EmptyIndicator />
        ),
    },
]

export const PastGrantsReport: FC = () => {
    const intl = useIntl()
    const [selectedApp, setSelectedApp] = useState<AppValue | null>(null)
    const [selectedEntitlement, setSelectedEntitlement] = useState<AppEntitlementValue | null>(null)

    // Construct the request based on the selected app and entitlement
    const request = useMemo(() => {
        const req = SearchPastGrantsRequest.create({
            expandMask: { paths: ['app_user_id', 'app_id'] },
        })

        // If an app is selected, filter by app ID
        if (selectedApp?.id) {
            req.appIds = [selectedApp.id]
        }

        // Only add the app entitlement ref if both app and entitlement are selected
        if (selectedApp?.id && selectedEntitlement?.id) {
            req.appEntitlementRefs = [
                AppEntitlementRef.create({
                    appId: selectedApp.id,
                    id: selectedEntitlement.id,
                }),
            ]
        } else {
            req.appEntitlementRefs = []
        }

        return req
    }, [selectedApp, selectedEntitlement])

    const onSelectedApp = useCallback((app: AppValue | null): void => {
        setSelectedApp(app)
        // Clear entitlement selection when app changes
        setSelectedEntitlement(null)
    }, [])

    const onSelectedEntitlement = useCallback((entitlement: AppEntitlementValue | null): void => {
        setSelectedEntitlement(entitlement)
    }, [])

    const table = useMemo(() => {
        return <C1TableReduxHook<SearchPastGrantsRequest, AppEntitlementUserBindingHistoryValue>
            cols={cols}
            request={request}
            hook={searchPastGrantsApi.useSearchPastGrants}
            invalidateHook={searchPastGrantsApi.useInvalidateSearchPastGrants}
            emptyState={<EmptyList
                text={<FormattedMessage
                    id="components_past_grants"
                    defaultMessage="No past grants"
                />}
            />}
        />
    }, [request])

    const exportDataActionRequest = useMemo(() => ExportDataRequest.create({
        queryId: QueryId.PAST_GRANTS,
        request: {
            oneofKind: 'searchPastGrantsRequest',
            searchPastGrantsRequest: request,
        },
    }), [request])

    return <>
        <Stack spacing={2} direction="row" sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: '300px' }}>
                    <SelectOneAppHook
                        onSelected={onSelectedApp}
                        value={selectedApp}
                        label={intl.formatMessage({ id: 'components_app_filter_label', defaultMessage: 'App' })}
                    />
                </Box>
                <Box sx={{ width: '300px' }}>
                    <SelectOneAppEntitlementHook
                        disabled={!selectedApp}
                        onSelected={onSelectedEntitlement}
                        baseRequest={{ appIds: selectedApp ? [selectedApp.id] : [] }}
                        label={intl.formatMessage({ id: 'components_entitlement_filter_label', defaultMessage: 'Entitlement' })}
                    />
                </Box>
            </Stack>
            <ExportData exportDataRequest={exportDataActionRequest} />
        </Stack>
        <List<AppEntitlementWithExpired> table={table} />
    </>
}
